import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  DataOfferCatalogDetailDto,
  DataOfferCatalogItemDto
} from '../../models/bautagebuch.models';
import { BautagebuchService } from '../../services/bautagebuch.service';
import {
  DataspaceWorkspaceStat,
  GuIncomingReportListItemVm
} from '../../models/dataspace-workspace.models';
import { prettyDataspaceSectionTitle } from '../../utils/dataspace-section-summary.util';
import { PurchaseDialogComponent, PurchaseDialogData } from './components/purchase-dialog/purchase-dialog.component';

@Component({
  standalone: false,
  selector: 'app-dataspace-gu-inbox-view',
  templateUrl: './dataspace-gu-inbox-view.component.html',
  styleUrls: ['./dataspace-gu-inbox-view.component.scss']
})
export class DataspaceGuInboxViewComponent implements OnChanges {
  @Input() organisationId: number | null = null;
  @Input() organisationName = '';
  @Input() refreshNonce = 0;

  loading = false;
  error = '';
  exportBusy = false;
  items: GuIncomingReportListItemVm[] = [];
  selectedItem: GuIncomingReportListItemVm | null = null;
  detail: DataOfferCatalogDetailDto | null = null;
  catalogSearchQuery = '';
  expandedOfferId: string | null = null;
  policyExpanded = false;

  // Dashboard data
  dashboardReportsPerProject: Array<{ name: string; value: number }> = [];
  dashboardStatusBreakdown: Array<{ name: string; value: number }> = [];
  dashboardSectionCoverage: Array<{ name: string; value: number }> = [];
  dashboardColorScheme = { domain: ['var(--app-primary)', 'var(--app-primary)', 'var(--app-primary)', 'var(--app-accent)', 'rgba(var(--app-on-surface-rgb), 0.67)'] };

  constructor(
    private readonly bautagebuchService: BautagebuchService,
    private readonly dialog: MatDialog,
    private readonly cd: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.organisationId || changes.refreshNonce) && this.organisationId) {
      this.loadCatalog();
    }
  }

  get effectiveDetail(): Partial<DataOfferCatalogDetailDto> | null {
    if (this.detail) return this.detail;
    return (this.selectedItem?.rawItem as any) || null;
  }

  get stats(): DataspaceWorkspaceStat[] {
    return [
      { label: 'Angebote', value: this.items.length },
      {
        label: 'Berichte',
        value: this.items.reduce((sum, item) => sum + (item.reportCount || 0), 0)
      }
    ];
  }

  get statusBadgeLabel(): string {
    if (!this.detail) {
      return 'Katalog Angebot';
    }
    if (this.detail.paymentRequired && !this.detail.paymentGranted) {
      return 'Zahlung erforderlich';
    }
    if (this.detail.exportAllowed) {
      return 'Export verfügbar';
    }
    return this.detail.contentAccessible ? 'Ansicht verfügbar' : 'Compute-to-Data';
  }

  get statusBadgeClass(): string {
    if (!this.detail) {
      return 'status-badge--offer';
    }
    if (this.detail.paymentRequired && !this.detail.paymentGranted) {
      return 'status-badge--warning';
    }
    if (this.detail.exportAllowed) {
      return 'status-badge--active';
    }
    return 'status-badge--offer';
  }

  loadCatalog(): void {
    if (!this.organisationId) {
      this.items = [];
      this.selectedItem = null;
      this.detail = null;
      this.clearDashboard();
      return;
    }

    this.loading = true;
    this.error = '';

    this.bautagebuchService.getDataOfferCatalog(this.organisationId, {
      projectKey: this.catalogSearchQuery || undefined
    }).subscribe({
      next: (items) => {
        this.items = (items || [])
          .map((item) => this.toCatalogItem(item));
        this.selectedItem = this.items[0] || null;
        this.loading = false;
        this.cd.detectChanges();
        if (this.selectedItem) {
          this.selectItem(this.selectedItem);
        } else {
          this.detail = null;
          this.clearDashboard();
          this.cd.detectChanges();
        }
      },
      error: (error) => {
        this.loading = false;
        this.error = error?.message || 'Katalog konnte nicht geladen werden.';
        this.cd.detectChanges();
      }
    });
  }

  selectItem(item: GuIncomingReportListItemVm): void {
    this.selectedItem = item;
    this.detail = null;
    this.error = '';
    this.policyExpanded = false;
    this.clearDashboard();
    this.cd.detectChanges();
    if (!this.organisationId) return;

    const requestedOfferId = item.offerId;
    this.bautagebuchService.getDataOfferCatalogDetail(this.organisationId, item.offerId).subscribe({
      next: (detail) => {
        if (this.selectedItem?.offerId !== requestedOfferId) return;
        this.detail = detail;
        this.syncSelectedItem(detail);
        this.buildDashboard();
        this.cd.detectChanges();
        this.scheduleChartRefresh();
      },
      error: () => {
        if (this.selectedItem?.offerId !== requestedOfferId) return;
        this.detail = null;
        this.clearDashboard();
        this.error = 'Offer-Details konnten nicht geladen werden.';
        this.cd.detectChanges();
      }
    });
  }

  toggleItemExpand(item: GuIncomingReportListItemVm, event: MouseEvent): void {
    event.stopPropagation();
    this.expandedOfferId = this.expandedOfferId === item.offerId ? null : item.offerId;
  }

  prettySection(sectionId: string): string {
    return prettyDataspaceSectionTitle(sectionId);
  }

  openPurchaseDialog(): void {
    if (!this.organisationId || !this.detail) {
      return;
    }

    const usage = this.usagePermissionLabel();
    const pricing = this.detail.pricing?.type === 'PAID'
      ? `${(this.detail.pricing.amount || 0)} ${this.detail.pricing.currency || ''}`.trim()
      : 'Kostenfrei';
    const validity = 'Nicht begrenzt';

    const dialogData: PurchaseDialogData = {
      organisationId: this.organisationId,
      offerId: this.detail.offerId,
      offerTitle: this.detail.title,
      producerOrgName: this.detail.producerOrgName,
      pricingLabel: pricing,
      permissionsLabel: usage,
      validityLabel: validity,
      reportCount: this.detail.resolvedReportCount || 0
    };

    const dialogRef = this.dialog.open(PurchaseDialogComponent, {
      width: '480px',
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: DataOfferCatalogDetailDto | null) => {
      if (result) {
        this.detail = result;
        this.syncSelectedItem(result);
        this.buildDashboard();
        this.cd.detectChanges();
        this.scheduleChartRefresh();
      }
    });
  }

  exportOffer(format: 'PDF' | 'XML'): void {
    if (!this.organisationId || !this.detail?.offerId || !this.detail.exportAllowed || this.exportBusy) {
      return;
    }

    this.exportBusy = true;
    this.error = '';
    this.bautagebuchService.exportDataOfferCatalog(this.organisationId, this.detail.offerId, format).subscribe({
      next: (result) => {
        this.exportBusy = false;
        this.downloadExport(result.fileName, result.mimeType, result.content, result.contentEncoding);
      },
      error: (error) => {
        this.exportBusy = false;
        this.error = error?.message || 'Export konnte nicht erzeugt werden.';
      }
    });
  }

  trackByAssetId(_: number, item: GuIncomingReportListItemVm): string {
    return item.offerId;
  }

  get policyOneLiner(): string {
    if (!this.detail) {
      return this.selectedItem?.policySummary || '';
    }
    const usage = this.usagePermissionLabel();
    const pricing = this.detail.pricing?.type === 'PAID'
      ? `${(this.detail.pricing.amount || 0)} ${this.detail.pricing.currency || ''}`.trim()
      : 'kostenfrei';
    return `${usage} · ${pricing}`;
  }

  get securedViewerTooltip(): string {
    if (this.detail?.contentAccessible) {
      return 'Secured Viewer: Direkte Einsicht in die aktuell aufgelösten Berichte.';
    }
    if (this.detail?.paymentRequired && !this.detail?.paymentGranted) {
      return 'Secured Viewer: Nach Fake Payment verfügbar.';
    }
    return 'Secured Viewer: Nicht aktiviert für dieses Offer.';
  }

  get computeToDataTooltip(): string {
    return this.detail?.usagePolicy?.mode === 'compute-to-data'
      ? 'Compute-to-Data: Dieses Offer ist für Compute-to-Data vorgesehen.'
      : 'Compute-to-Data: Optionaler Verarbeitungspfad für spätere Phasen.';
  }

  get downloadTooltip(): string {
    if (this.detail?.exportAllowed) {
      return 'Download: Export ist gemäß Offer-Regeln verfügbar.';
    }
    if (this.detail?.paymentRequired && !this.detail?.paymentGranted) {
      return 'Download: Nach Fake Payment freischaltbar.';
    }
    return 'Download: Deaktiviert gemäß aktuellem Offer.';
  }

  get dashboardPricingLabel(): string {
    if (!this.detail?.pricing || this.detail.pricing.type !== 'PAID') {
      return 'Kostenfrei';
    }
    return `${this.detail.pricing.amount || 0} ${this.detail.pricing.currency || ''}`.trim();
  }

  get dashboardReportDateRange(): string {
    const first = this.detail?.firstReportDate || this.selectedItem?.firstReportDate;
    const last = this.detail?.lastReportDate || this.selectedItem?.lastReportDate;
    if (first && last) {
      return `${this.formatDateLabel(first)} – ${this.formatDateLabel(last)}`;
    }
    if (first) {
      return `ab ${this.formatDateLabel(first)}`;
    }
    if (last) {
      return `bis ${this.formatDateLabel(last)}`;
    }
    return '–';
  }

  get policyFacts(): string[] {
    if (!this.detail) {
      return [];
    }
    const facts: string[] = [];
    facts.push(`Nutzung: ${this.usagePermissionLabel()}`);
    const exportFormats = this.exportFormats();
    if (exportFormats.length) {
      facts.push(`Export: ${exportFormats.join(', ')}`);
    }
    facts.push(
      this.detail.pricing?.type === 'PAID'
        ? `Preis: ${(this.detail.pricing.amount || 0) + ' ' + (this.detail.pricing.currency || '')} ${this.detail.pricing.billingBasis || ''}`.trim()
        : 'Preis: kostenfrei'
    );
    if (this.detail.paymentRequired) {
      facts.push(
        this.detail.paymentGranted
          ? 'Zahlung: bestätigt'
          : 'Zahlung: vor Zugriff erforderlich'
      );
    }
    if (this.detail.fakeOrganisationBypassActive) {
      facts.push('Demo-Sichtbarkeit: organisationsunabhängiger GU-Bypass aktiv');
    }
    if (this.detail.projectLabels?.length) {
      facts.push(`Projekte: ${this.detail.projectLabels.join(', ')}`);
    }
    return facts;
  }

  private usagePermissionLabel(): string {
    const permissions = this.detail?.usagePolicy?.permissions || [];
    if (permissions.includes('COMPUTE_TO_DATA') && !permissions.includes('VIEW')) {
      return 'Compute-to-Data';
    }
    const exportFormats = this.exportFormats();
    if (exportFormats.length) {
      return `Ansicht + ${exportFormats.join('/')} Export`;
    }
    return 'Ansicht';
  }

  private exportFormats(): string[] {
    const permissions = this.detail?.usagePolicy?.permissions || [];
    const formats: string[] = [];
    if (permissions.includes('EXPORT_PDF')) {
      formats.push('PDF');
    }
    if (permissions.includes('EXPORT_XML')) {
      formats.push('XML');
    }
    return formats;
  }

  private toCatalogItem(item: DataOfferCatalogItemDto): GuIncomingReportListItemVm {
    return {
      id: item.offerId,
      offerId: item.offerId,
      producerOrgName: item.producerOrgName,
      title: item.title,
      projectLabels: item.projectLabels || [],
      firstReportDate: item.firstReportDate,
      lastReportDate: item.lastReportDate,
      status: 'OFFER',
      updatedAt: item.updatedAt,
      reportCount: item.resolvedReportCount || 0,
      includedSections: item.includedSections || [],
      policySummary: item.policySummary || 'View-Only Policy',
      rawItem: item
    };
  }

  private syncSelectedItem(detail: DataOfferCatalogDetailDto): void {
    const selected = this.selectedItem;
    if (!selected) {
      return;
    }
    selected.policySummary = detail.policySummary || selected.policySummary;
    selected.reportCount = detail.resolvedReportCount || selected.reportCount;
    selected.rawItem = {
      ...(selected.rawItem || {}),
      ...detail
    };
  }

  private clearDashboard(): void {
    this.dashboardReportsPerProject = [];
    this.dashboardStatusBreakdown = [];
    this.dashboardSectionCoverage = [];
  }

  private scheduleChartRefresh(): void {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.cd.detectChanges();
    });
  }

  private buildDashboard(): void {
    const reports = this.detail?.resolvedReports || [];

    // Reports per project
    const projectMap = new Map<string, number>();
    reports.forEach((r) => {
      const label = r.projectLabel || 'Unbekannt';
      projectMap.set(label, (projectMap.get(label) || 0) + 1);
    });
    this.dashboardReportsPerProject = Array.from(projectMap.entries())
      .map(([name, value]) => ({ name, value }));

    // Status breakdown
    const statusMap = new Map<string, number>();
    reports.forEach((r) => {
      const status = r.reportLifecycleStatus || 'UNKNOWN';
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });
    this.dashboardStatusBreakdown = Array.from(statusMap.entries())
      .map(([name, value]) => ({ name, value }));

    // Section coverage
    const sectionMap = new Map<string, number>();
    reports.forEach((r) => {
      const payload = r.reportPayload || {};
      Object.keys(payload).filter((k) => k !== 'selectedSectionIds').forEach((key) => {
        sectionMap.set(
          prettyDataspaceSectionTitle(key),
          (sectionMap.get(prettyDataspaceSectionTitle(key)) || 0) + 1
        );
      });
    });
    this.dashboardSectionCoverage = Array.from(sectionMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }

  private formatDateLabel(value: string): string {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const [year, month, day] = value.substring(0, 10).split('-');
      return `${day}.${month}.${year}`;
    }
    return value;
  }

  private downloadExport(fileName: string, mimeType: string, content: string, contentEncoding?: string | null): void {
    const blob = contentEncoding === 'base64'
      ? new Blob([this.base64ToBytes(content) as BlobPart], { type: mimeType || 'application/octet-stream' })
      : new Blob([content], { type: mimeType || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private base64ToBytes(content: string): Uint8Array {
    const binary = window.atob(content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
